/**
 * Phase 2+ pages — embed the legacy Link admin tools inside the V2 shell
 * so operators have the full feature set under the new navigation.
 *
 * The body components keep their original behaviour; we wrap each one in
 * `AdminShell` so they inherit the navy + orange chrome and the 6-section
 * sidebar. Nothing about the underlying workflows changed.
 */
import React from "react";
import { Link } from "react-router-dom";

import AdminShell from "@/apps/admin/v2/AdminShell";

import AdminOps from "@/apps/admin/pages/AdminOps";
import AdminAvailability from "@/apps/admin/pages/AdminAvailability";
import AdminCustomers from "@/apps/admin/pages/AdminCustomers";
import AdminDashboard from "@/apps/admin/pages/AdminDashboard";
import AdminWaitlist from "@/apps/admin/pages/AdminWaitlist";
import AdminCheckoutFailures from "@/apps/admin/pages/AdminCheckoutFailures";
import AdminBlocks from "@/apps/admin/pages/AdminBlocks";

export function OperationsPage() {
  return (
    <AdminShell title="Operations" subtitle="Today's rides, dispatch and trip management.">
      <div className="bg-white rounded-2xl border border-[#0F172A]/[0.08] p-2 -m-2"><AdminOps /></div>
    </AdminShell>
  );
}

export function AvailabilityPage() {
  return (
    <AdminShell title="Availability" subtitle="Block AM/PM slots and full-day operations holds.">
      <div className="bg-white rounded-2xl border border-[#0F172A]/[0.08] p-2 -m-2"><AdminAvailability /></div>
    </AdminShell>
  );
}

export function CustomersPage() {
  return (
    <AdminShell title="Customers" subtitle="Rider directory, founding-rider toggle, account status.">
      <div className="bg-white rounded-2xl border border-[#0F172A]/[0.08] p-2 -m-2"><AdminCustomers /></div>
    </AdminShell>
  );
}

export function DriversPage() {
  return (
    <AdminShell title="Drivers" subtitle="Driver roster, assignment, and lifecycle management.">
      <div className="bg-white rounded-2xl border border-[#0F172A]/[0.08] p-2 -m-2"><AdminDashboard /></div>
    </AdminShell>
  );
}

export function WaitlistPage() {
  return (
    <AdminShell title="Waitlist" subtitle="Approve or reject rider applications.">
      <div className="bg-white rounded-2xl border border-[#0F172A]/[0.08] p-2 -m-2"><AdminWaitlist /></div>
    </AdminShell>
  );
}

export function FinancePage() {
  return (
    <AdminShell title="Finance" subtitle="Stripe activity, checkout failures, payment forensics.">
      <div className="bg-white rounded-2xl border border-[#0F172A]/[0.08] p-2 -m-2"><AdminCheckoutFailures /></div>
    </AdminShell>
  );
}

export function BlocksLegacyPage() {
  return (
    <AdminShell title="Blocks" subtitle="Phase 3 driver block inventory (legacy view).">
      <div className="bg-white rounded-2xl border border-[#0F172A]/[0.08] p-2 -m-2"><AdminBlocks /></div>
    </AdminShell>
  );
}

export function SettingsHomePage() {
  return (
    <AdminShell title="Settings" subtitle="Manage the rules that power the marketplace.">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <SettingCard to="/admin/v2/settings/sectors" eyebrow="Live" title="Sector Manager" sub="Draw / edit / merge dispatch territories · demand weight · surge multiplier" />
        <SettingCard to="/admin/v2/waitlist" eyebrow="Live" title="Waitlist Queue" sub="Approve / reject rider applications" />
        <SettingCard to="/admin/v2/availability" eyebrow="Live" title="Availability Blockouts" sub="Block AM/PM slots and full-day holds" />
        <SettingCard to="/admin/v2/customers" eyebrow="Live" title="Customer directory" sub="Founding rider toggle · billing · activity" />
        <SettingCard eyebrow="Phase 3" title="Pricing Rules" sub="Base fare · mileage rate · wait fee · cancellation fee · sector multipliers" disabled />
        <SettingCard eyebrow="Phase 3" title="Driver Rules" sub="Cancellation policies · eligibility · vehicle requirements · payout schedule" disabled />
      </div>
    </AdminShell>
  );
}

function SettingCard({ to, eyebrow, title, sub, disabled }) {
  const body = (
    <div className={`rounded-2xl bg-white border border-[#0F172A]/[0.08] p-5 ${disabled ? "opacity-60" : "hover:border-[#F97316]/40"}`}>
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#F97316] font-semibold">{eyebrow}</div>
      <div className="font-serif text-[18px] text-[#081B4B] mt-1">{title}</div>
      <div className="text-[12px] text-[#475569] mt-1.5">{sub}</div>
    </div>
  );
  if (disabled || !to) return body;
  return <Link to={to} data-testid={`settings-card-${to.split("/").pop()}`}>{body}</Link>;
}
